//NPM LIBRARIES
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Row, Col } from 'reactstrap';
import queryString from 'query-string';
import { AUcallout } from '@gov.au/callout';
//REDUX ACTIONS AND SELECTORS
import {
    getFormDataSelector,
    getFormPropertiesSelector,
    getFormServiceStatusSelector,
} from '../FormStore/Selectors/FormDataSelectors';
import { getB2cSettingsSelector } from '../PortalStore/Selectors/SystemSettingSelectors';
import { addPageAlert, clearPageAlert } from '../PortalStore/Actions/PageAlertActions';
import { verifyAcrConfirmationUser } from '../FormStore/Actions/FormServiceActions/Aircraft/ValidateAcrConfirmationUserAction';
import { VERIFY_USER_ROLE_SUCCESS } from '../FormStore/Actions/FormServiceActions/IndustryAssist/VerifyUserRoleAction';
import { acrConfirmationSubmission } from '../FormStore/Actions/FormServiceActions/Aircraft/AcrConfirmationSubmissionAction';
import { SUBMISSION_SUCCESS } from '../FormStore/Actions/FormServiceActions/GenericFormServiceActions/SubmissionAction';

//COMPONENTS AND UTILITIES
import Application from '../components/CASAForm/Application';
import Step from '../components/CASAForm/Step';
import Question from '../components/CASAForm/Question';
import CASASpinner from '../components/Portal/CASASpinner';
import SuccessScreen from '../pages/SuccessScreen';

const declarationValue = [
    {
        name: "confirmedCorrectDetails",
        id: "confirmedCorrectDetails",
        value: "confirmedCorrectDetails",
        optionLabel: "I confirm all the details relating to my aircraft registration are correct."
    },
    {
        name: "incorrectDetails",
        id: "incorrectDetails",
        value: "incorrectDetails",
        optionLabel: "I confirm some or all details relating to my aircraft registration have changed. I understand that I must inform CASA of these changes as soon as possible and in any event not later than 14 days."
    }
]

const declarationText =
    <p>As an aircraft registration holder / as authorised representative of an aircraft registration holder, I acknowledge that aircraft registration holders have obligations under civil aviation legislation to inform CASA about information in relation to my aircraft that is not correct, within 14 days after finding out about that information not being correct. <br/> <br/>
    I am aware and accept the risk that information sent electronically may be intercepted and read during transmission, not delivered or modified. <br/> <br/>
    I have read <a href="https://www.casa.gov.au/node/37026">CASA’s policy on the collection and release of information</a> and I authorise CASA to release information contained on this form in accordance with that policy.</p>


class AircraftConfirmation extends Component {
    constructor(props) {
        super(props);

        const urlParams = this.processUrlParameters(window.location.search);

        this.state = {
            formLoaded: false,
            applicationSubmitting: false,
            organisationArn: urlParams?.organisationArn ?? null,
            organisationId: urlParams?.organisationId ?? null,
            enableForm: false,
            showPageAlert: false,
            link: urlParams?.breadcrumbs[1].link ?? null,
            urlParams: urlParams
        }
    }

    componentDidMount() {
        this.props.dispatch(clearPageAlert());

        if (!this.state.formLoaded) {
            this.loadForm();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.formProperties.submitForm &&
            prevProps.formProperties.submitForm !== this.props.formProperties.submitForm) {
            this.aircraftConfirmationSubmission()
        }
    }

    // TODO: Pull this out to a common function
    processUrlParameters = (windowUrl) => {
        let urlParameters = queryString.parse(windowUrl)
        if (urlParameters !== undefined) {
            return JSON.parse(atob(decodeURIComponent(urlParameters.params)))
        }
    }

    loadForm = () => {
        if (this.state.organisationArn) {
            const webappurlForms = this.props.b2cSettings.webappurlFormsApi;

            this.props.dispatch(
                verifyAcrConfirmationUser(webappurlForms, this.props.b2cSettings.accesstokenForms, this.state.organisationArn))
                .then(response => {
                    if (response.type === VERIFY_USER_ROLE_SUCCESS) {
                        this.setState({ formLoaded: true, enableForm: true });
                    } else {
                        this.setState({ formLoaded: true, enableForm: false });
                        this.props.dispatch(addPageAlert({
                            level: "error",
                            title: "Unable to complete application online",
                            message: 'Unfortunately this application cannot be completed online. Please contact us for further information.'
                        }));
                    }
                })
                .catch(() => {
                    this.props.dispatch(addPageAlert({
                        level: "error",
                        title: "Unable to complete application online",
                        message: 'Unfortunately this application cannot be completed online. Please contact us for further information.'
                    }));
                    this.setState({ formLoaded: true, enableForm: false });
                })
        } else {
            this.setState({ formLoaded: true, enableForm: true });
        }
    }

    aircraftConfirmationSubmission = () => {
        const webappurlForms = this.props.b2cSettings.webappurlFormsApi;
        this.setState({applicationSubmitting: true})

        if (this.props.formData) {
            this.props.dispatch(
                acrConfirmationSubmission(webappurlForms, this.props.b2cSettings.accesstokenForms, this.props.formProperties, this.props.formData, declarationText)
            )
                .then(response => {
                    const questions = this.props.formData.steps[0].questions;
                    const declaration = Object.values(questions).find(question => question.id === 'declaration').answer.radioValue;
                    if (response.type === SUBMISSION_SUCCESS) {
                        this.setState({applicationSubmitting: false})
                        if (declaration === 'confirmedCorrectDetails') {
                            this.props.dispatch(addPageAlert({
                                level: 'success',
                                title: 'Aircraft registration detail confirmation successful',
                                message: ''
                            }));
                        } else {
                            this.props.dispatch(addPageAlert({
                                level: 'info',
                                title: 'Aircraft registration confirmation successful',
                                message: `<p><strong>You have indicated that some details of your registered aircraft have changed.
                                </strong></p><p>You must inform CASA of these changes within 14 days. Please refer to the 
                                <a href="https://www.casa.gov.au/aircraft/civil-aircraft-register/aircraft-registration">CASA website</a> for guidance on how to inform CASA of these changes.</p>`
                            }));
                        }
                    } else {
                        this.setState({applicationSubmitting: false})
                        this.props.dispatch(addPageAlert({
                            level: "error",
                            title: "Unable to complete application online",
                            message: 'Unfortunately this application cannot be completed online. Please contact us for further information.'
                        }));
                    }
                })
        }
    }

    setupSuccessContent = () => {
        const questions = this.props.formData.steps[0].questions;
        const declaration = Object.values(questions).find(question => question.id === 'declaration').answer.radioValue;

        if (declaration === 'confirmedCorrectDetails') {
            return (
                <p>Your aircraft registration details have been confirmed and a copy of this 
                    confirmation has been sent to your email address <strong>{this.props.userdetails.items.value[0].emailaddress1}</strong>.</p>
            )
        } else {
            return (
                <p>Confirmation of this submission and information on how to inform CASA of 
                    these changes have been sent to your email address <strong>{this.props.userdetails.items.value[0].emailaddress1}</strong>.</p>
            )
        }
    }

    render() {

        const breadcrumbs = [
            { text: 'myCASA', link: this.state.urlParams.breadcrumbs[0].link },
            { text: 'Manage aircraft', link: this.state.urlParams.breadcrumbs[1].link },
            { text: 'Confirm aircraft registration' }
        ]
        if (this.props.submissionStatus.submissionSucceeded) {
            return (
                <SuccessScreen>
                    {this.setupSuccessContent()}
                </SuccessScreen>
            )
        }
        return (
            <Fragment>
                {this.state.applicationSubmitting ? <CASASpinner message='Submitting form' /> : null}
                {this.state.formLoaded ?
                    <Application
                        breadcrumbs={breadcrumbs}
                        formInformation={{
                            id: "aircraftConfirmatiom",
                            name: "Aircraft Registration Confirmation",
                            title: "Confirm aircraft registration",
                            rootFormCode: "ACR_CON",
                            formCode: "ACR_CON_NCH",
                            formDataType: "genericFormData",
                            version: "01"
                        }}
                        enableSaveButton={false}
                        showback={false}
                        enableForm={this.state.enableForm}>

                        <Step
                            key="aircraftConfirmation"
                            id="aircraftConfirmation"
                            stepTitle=""
                            nextStep={this.formStepNavigation}
                            initialStep
                            isLastStep
                            showback={true}
                            backurl={this.state.link}
                            submitText="Submit and confirm registration status"
                        >

                            <Row noGutters>
                                <Col lg={12} md={12} className="float-none">
                                    <AUcallout
                                        title=""
                                        srOnlyTitle>
                                            <h3 className="au-callout__heading">Aircraft details all correct and up to date?</h3>
                                            <p>If your aircraft registration details are correct, please select this option in the declaration below.</p>
                                            <h3 className="au-callout__heading">Some aircraft details have changed?</h3>
                                            <p> If details about your aircraft manufacturer, aircraft type, serial number or registration holder / registered operator have changed, <a href="https://www.casa.gov.au/aircraft/civil-aircraft-register/aircraft-registration">
                                                you must inform CASA of these changes</a>. If your aircraft is no longer registered or will no longer be registered, you must submit a <a href="https://www.casa.gov.au/aircraft/civil-aircraft-register/aircraft-registration">cancellation of aircraft registration</a>.
                                                Please select this option in the declaration below
                                            </p>
                                    </AUcallout>
                                </Col>
                            </Row>

                            <Question
                                id="declaration"
                                type="conditionalDeclaration"
                                name="declaration"
                                label="Please confirm the status of your aircraft details"
                                radioSet={declarationValue}
                                stepid="aircraftConfirmation"
                                colspan={12}>
                                {declarationText}
                            </Question>

                        </Step>
                    </Application> : <CASASpinner />}
            </Fragment>
        );
    }
}

AircraftConfirmation.propTypes = {
    children: PropTypes.array,
    dispatch: PropTypes.func,
    b2cSettings: PropTypes.object,
    userdetails: PropTypes.object,
    formProperties: PropTypes.object,
    formData: PropTypes.object,
    submissionStatus: PropTypes.object,
    envdata: PropTypes.object,
};

const mapStateToProps = (state) => {
    return {
        userdetails: state.userdetails,
        formProperties: getFormPropertiesSelector(state.formStore),
        formData: getFormDataSelector(state.formStore, 'genericFormData'),
        b2cSettings: getB2cSettingsSelector(state.B2CSettings, ['webappurlFormsApi', 'accesstokenForms', 'parsed', 'standalone']),
        envdata: state.envdata,
        submissionStatus: getFormServiceStatusSelector(state.formStore, 'submission')
    }
}

export default connect(mapStateToProps)(AircraftConfirmation);